import React, { useState, useEffect, useRef, useCallback } from "react";
import { connect } from "react-redux";
import "./autoLogoutTimeOut.scss";
import { useIdleTimer } from "react-idle-timer";
import { logoutUserRequest, refreshUserRedisCacheRequest, putHeartbeatRequest } from "../../components/login/LoginPageDucks";

interface AutoLogoutProps {
  logoutUser?: (action: any) => void;
  refreshUserCache?: () => void;
  sendHeartbeat?: () => void;
  userState?: any;
  sessionTimeoutMinutes?: number; // Configurable timeout
  warningTimeSeconds?: number; // Configurable warning time
}

const AutoLogoutTimeOut: React.FC<AutoLogoutProps> = ({ 
  logoutUser, 
  refreshUserCache, 
  sendHeartbeat,
  userState,
  sessionTimeoutMinutes = 20, // Default 20 minutes
  warningTimeSeconds = 60 // Default 60 seconds warning
}) => {
  const [inactive, setInactive] = useState(false);
  const [countdown, setCountdown] = useState(warningTimeSeconds);
  const countdownInterval = useRef<NodeJS.Timeout | null>(null);
  const heartbeatInterval = useRef<NodeJS.Timeout | null>(null);

  // Convert minutes to milliseconds for idle timer
  const timeoutMs = sessionTimeoutMinutes * 60 * 1000;

  // Get heartbeat interval from sessionStorage or default to 5 minutes
  const getHeartbeatInterval = useCallback(() => {
    const heartbeatId = sessionStorage.getItem("heartbeatID");
    return heartbeatId ? parseInt(heartbeatId) * 1000 : 5 * 60 * 1000; // Default 5 minutes
  }, []);

  // Start heartbeat when user is active
  const startHeartbeat = useCallback(() => {
    if (heartbeatInterval.current) {
      clearInterval(heartbeatInterval.current);
    }

    const interval = getHeartbeatInterval();
    heartbeatInterval.current = setInterval(() => {
      if (sendHeartbeat && userState?.userID) {
        sendHeartbeat();
      }
    }, interval);
  }, [sendHeartbeat, userState?.userID, getHeartbeatInterval]);

  // Stop heartbeat
  const stopHeartbeat = useCallback(() => {
    if (heartbeatInterval.current) {
      clearInterval(heartbeatInterval.current);
      heartbeatInterval.current = null;
    }
  }, []);

  // Handle user activity - refresh cache and reset heartbeat
  const handleUserActivity = useCallback(() => {
    if (refreshUserCache && userState?.userID) {
      refreshUserCache();
    }
    startHeartbeat();
  }, [refreshUserCache, userState?.userID, startHeartbeat]);

  // Function to log out the user
  const handleLogout = useCallback(() => {
    stopHeartbeat();
    if (logoutUser && userState?.userAttributes?.id) {
      logoutUser({ 
        userId: userState.userAttributes.id, 
        redirect: () => window.location.replace('/' )
      });
    }
    setInactive(false);
    if (countdownInterval.current) {
      clearInterval(countdownInterval.current);
    }
  }, [logoutUser, userState?.userAttributes?.id, stopHeartbeat]);

  // Function to reset inactivity
  const resetInactivity = useCallback(() => {
    setInactive(false);
    setCountdown(warningTimeSeconds);
    if (countdownInterval.current) {
      clearInterval(countdownInterval.current);
      countdownInterval.current = null;
    }
    handleUserActivity();
  }, [warningTimeSeconds, handleUserActivity]);

  // Initialize Idle Timer - include more events (wheel/pointer/visibilitychange)
  const { reset } = useIdleTimer({
    timeout: timeoutMs,
    onIdle: () => {
      stopHeartbeat();
      setInactive(true);
    },
    onActive: resetInactivity,
    debounce: 500,
    events: [
      "click",
    ],
    crossTab: false,
    startOnMount: true,
    startManually: false,
  });

  // Countdown when inactive
  useEffect(() => {
    if (inactive && countdown > 0 && !countdownInterval.current) {
      countdownInterval.current = setInterval(() => {
        setCountdown((prev) => {
          if (prev <= 1) {
            clearInterval(countdownInterval.current!);
            handleLogout();
            return 0;
          }
          return prev - 1;
        });
      }, 1000);
    }

    return () => {
      if (countdownInterval.current) {
        clearInterval(countdownInterval.current);
        countdownInterval.current = null;
      }
    };
  }, [inactive, countdown, handleLogout]);

  // Start heartbeat on mount if user is logged in
  useEffect(() => {
    if (userState?.userID) {
      startHeartbeat();
    }

    return () => {
      stopHeartbeat();
    };
  }, [userState?.userID, startHeartbeat, stopHeartbeat]);

  // Handle continue session
  const handleContinueSession = useCallback((event: React.MouseEvent<HTMLButtonElement>) => {
    event.stopPropagation();
    resetInactivity();
    reset(); // Reset the idle timer
  }, [resetInactivity, reset]);

  // Don't render if user is not logged in
  if (!userState?.userID) {
    return null;
  }

  return (
    <>
      {inactive && (
        <div id="autoLogoutOverlay">
          <div id="autoLogoutModal">
            <p id="autoLogoutHeader">Your session is about to expire</p>
            <p id="autoLogoutCountdown">
              You will be logged out in <span id="autoLogoutDigits">{countdown}</span> seconds
            </p>
            <div id="autoLogoutButtons">
              <button id="autoLogoutContinueButton" onClick={handleContinueSession}>
                Continue Session
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

// Redux connection
function mapStateToProps(state: any) {
  return {
    userState: state.loginState,
  };
}

function mapDispatchToProps(dispatch: Function) {
  return {
    logoutUser: (action: any) => dispatch(logoutUserRequest(action)),
    refreshUserCache: () => dispatch(refreshUserRedisCacheRequest()),
    sendHeartbeat: () => dispatch(putHeartbeatRequest()),
  };
}

export default connect(mapStateToProps, mapDispatchToProps)(AutoLogoutTimeOut);